import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { BillboardListParams } from '@/features/billboards/api/billboardApi';
import { useDynamicVenues } from '@/features/billboards/api/useBillboardQueries';
import {
  findLocationTypes,
  findMidOptions,
  getMergedMidOptions,
  getMergedSubOptions,
  getMidLabel,
  getSubLabel,
  toChip,
  type HierarchyChip,
  type VenueHierarchy,
} from '@/features/billboards/types/hierarchy';
import {
  ORIENTATION_OPTIONS,
  SORT_OPTIONS,
} from '@/features/billboards/utils/billboardsListUtils';
import { NEARBY_DEFAULT_RADIUS } from '@/features/billboards/utils/nearbyBillboardsUtils';
import {
  LocalityRadiusField,
  type LocalityPlace,
} from '@/features/billboards/components/list/LocalityRadiusField';

type BillboardListFiltersProps = {
  open: boolean;
  filters: BillboardListParams;
  city?: string;
  onClose: () => void;
  onApply: (next: Partial<BillboardListParams>) => void;
  onClear: () => void;
};

function asFilterArray(value?: string | string[]): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value];
}

function toLocalityPlace(filters: BillboardListParams): LocalityPlace {
  return {
    label: filters.locality || '',
    lat: filters.lat ?? null,
    lng: filters.lng ?? null,
    radius: filters.radius || NEARBY_DEFAULT_RADIUS,
  };
}

export function BillboardListFilters({
  open,
  filters,
  city,
  onClose,
  onApply,
  onClear,
}: BillboardListFiltersProps) {
  const [draft, setDraft] = useState(filters);
  const [locality, setLocality] = useState<LocalityPlace>(() =>
    toLocalityPlace(filters),
  );
  const { data, isPending, isError } = useDynamicVenues({}, city);

  useEffect(() => {
    if (open) {
      setDraft(filters);
      setLocality(toLocalityPlace(filters));
    }
  }, [open, filters]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const hierarchy = data?.data as VenueHierarchy | undefined;
  const locationTypes = (hierarchy?.locationTypes || []).map(toChip);
  const selectedLocations = findLocationTypes(
    hierarchy,
    asFilterArray(draft.locationType),
  );
  const mergedMids = getMergedMidOptions(selectedLocations);
  const midOptions = mergedMids.map(toChip);
  const selectedMids = findMidOptions(
    selectedLocations,
    asFilterArray(draft.venueType),
  );
  const subOptions = getMergedSubOptions(selectedMids).map(toChip);
  const midLabel = getMidLabel(selectedLocations[0]);
  const subLabel = getSubLabel(selectedMids[0]);

  const locationValues = asFilterArray(draft.locationType);
  const venueValues = asFilterArray(draft.venueType);
  const subValues = asFilterArray(draft.VenueSubType);

  const updateHierarchy = (
    key: 'locationType' | 'venueType' | 'VenueSubType',
    value: string[],
  ) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };

      if (key === 'locationType') {
        const locations = findLocationTypes(hierarchy, value);
        const allowedMids = new Set(
          getMergedMidOptions(locations).flatMap((item) => [
            item.key,
            item.label,
          ]),
        );
        const nextVenue = asFilterArray(prev.venueType).filter((item) =>
          allowedMids.has(item),
        );
        const selectedMids = findMidOptions(locations, nextVenue);
        const allowedSubs = new Set(
          getMergedSubOptions(selectedMids).flatMap((item) => [
            item.key,
            item.label,
          ]),
        );
        next.venueType = nextVenue;
        next.VenueSubType = asFilterArray(prev.VenueSubType).filter((item) =>
          allowedSubs.has(item),
        );
        return next;
      }

      if (key === 'venueType') {
        const locations = findLocationTypes(
          hierarchy,
          asFilterArray(prev.locationType),
        );
        const selectedMids = findMidOptions(locations, value);
        const allowedSubs = new Set(
          getMergedSubOptions(selectedMids).flatMap((item) => [
            item.key,
            item.label,
          ]),
        );
        next.VenueSubType = asFilterArray(prev.VenueSubType).filter((item) =>
          allowedSubs.has(item),
        );
      }

      return next;
    });
  };

  const updateDraft = (key: keyof BillboardListParams, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    const hasCoords =
      Number.isFinite(locality.lat) && Number.isFinite(locality.lng);
    onApply({
      locationType: locationValues,
      venueType: venueValues,
      VenueSubType: subValues,
      screenOrientation: draft.screenOrientation || '',
      sortBy: draft.sortBy || '',
      locality: locality.label ? locality.label.trim() : '',
      lat: hasCoords ? locality.lat : null,
      lng: hasCoords ? locality.lng : null,
      radius: hasCoords ? locality.radius || NEARBY_DEFAULT_RADIUS : undefined,
      areaFilter: hasCoords ? true : undefined,
      area: draft.area?.trim() || undefined,
    });
  };

  const handleClear = () => {
    setDraft({
      ...filters,
      locationType: [],
      venueType: [],
      VenueSubType: [],
      screenOrientation: '',
      sortBy: '',
      area: '',
      locality: '',
      lat: null,
      lng: null,
      radius: undefined,
      areaFilter: undefined,
    });
    setLocality({
      label: '',
      lat: null,
      lng: null,
      radius: NEARBY_DEFAULT_RADIUS,
    });
    onClear();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close filters"
        className="absolute inset-0 cursor-pointer bg-[rgba(30,27,58,0.5)] backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-[560px] flex-col overflow-hidden rounded-t-[28px] bg-white shadow-[0_30px_80px_rgba(20,15,60,0.4)] sm:rounded-[28px]">
        <div className="flex items-start justify-between border-b border-[#F3F1FA] px-7 pt-6 pb-4">
          <div className="text-left">
            <h2 className="text-xl font-extrabold text-[#3A3660]">Filters</h2>
            <p className="mt-1 text-[13px] font-medium text-[#6A6688]">Refine your screen search</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-[#EAE8F5] bg-white text-[#4A4670] shadow-sm transition hover:bg-[#F3F2F9]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto px-7 py-5">
          <LocalityRadiusField
            value={locality}
            city={city}
            onChange={setLocality}
          />

          <Field label="Area">
            <input
              type="text"
              value={draft.area || ''}
              onChange={(e) => updateDraft('area', e.target.value)}
              placeholder="Search by area (e.g. Kolar Road)…"
              className="h-12 w-full rounded-[14px] border border-[#EAE8F5] bg-white px-3.5 text-sm text-[#3A3660] placeholder-[#9CA3AF] outline-none transition focus:border-[#8156B4]"
              aria-label="Search by area"
            />
          </Field>

          <Field label="Orientation">
            <div className="relative">
              <select
                value={draft.screenOrientation || ''}
                onChange={(e) => updateDraft('screenOrientation', e.target.value)}
                className="h-12 w-full cursor-pointer appearance-none rounded-[14px] border border-[#EAE8F5] bg-white px-3.5 pr-9 text-sm font-semibold text-[#3A3660] outline-none transition focus:border-[#8156B4]"
              >
                {ORIENTATION_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6A6688]"
                aria-hidden
              />
            </div>
          </Field>

          <Field label="Sort by">
            <div className="relative">
              <select
                value={draft.sortBy || ''}
                onChange={(e) => updateDraft('sortBy', e.target.value)}
                className="h-12 w-full cursor-pointer appearance-none rounded-[14px] border border-[#EAE8F5] bg-white px-3.5 pr-9 text-sm font-semibold text-[#3A3660] outline-none transition focus:border-[#8156B4]"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value || 'default'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6A6688]"
                aria-hidden
              />
            </div>
          </Field>

          <Field label="Location type">
            <ChipGroup
              loading={isPending && !locationTypes.length}
              error={isError}
              options={locationTypes}
              value={locationValues}
              onChange={(value) => updateHierarchy('locationType', value)}
            />
          </Field>

          {locationValues.length > 0 && midOptions.length > 0 ? (
            <Field label={midLabel}>
              <ChipGroup
                options={midOptions}
                value={venueValues}
                onChange={(value) => updateHierarchy('venueType', value)}
              />
            </Field>
          ) : null}

          {venueValues.length > 0 && subOptions.length > 0 ? (
            <Field label={subLabel}>
              <ChipGroup
                options={subOptions}
                value={subValues}
                onChange={(value) => updateHierarchy('VenueSubType', value)}
              />
            </Field>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-[#F3F1FA] px-7 py-4">
          <button
            type="button"
            onClick={handleClear}
            className="h-11 px-6 cursor-pointer rounded-[14px] border border-[#D9D3F0] bg-white text-sm font-bold text-[#2B3E8A] transition hover:bg-[#F4F2FB]"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="h-11 px-7 cursor-pointer rounded-full border-none text-sm font-bold text-white shadow-sm transition hover:opacity-95"
            style={{ background: 'linear-gradient(135deg, #3B66B8, #8156B4)' }}
          >
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="text-left">
      <p className="mb-1.5 text-[11px] font-extrabold tracking-[1px] text-[#6A6688] uppercase">
        {label}
      </p>
      {children}
    </div>
  );
}

function ChipGroup({
  options,
  value,
  onChange,
  loading,
  error,
}: {
  options: HierarchyChip[];
  value: string[];
  onChange: (value: string[]) => void;
  loading?: boolean;
  error?: boolean;
}) {
  if (loading) {
    return <p className="text-sm text-[#9CA3AF]">Loading options…</p>;
  }

  if (error) {
    return (
      <p className="text-sm text-[#B42318]">
        Couldn’t load filter options. Check API connection.
      </p>
    );
  }

  if (!options.length) {
    return <p className="text-sm text-[#9CA3AF]">No options available</p>;
  }

  const toggle = (key: string) => {
    onChange(
      value.includes(key) ? value.filter((item) => item !== key) : [...value, key],
    );
  };

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const active = value.includes(option.key) || value.includes(option.label);
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => toggle(option.key)}
            className={`cursor-pointer rounded-full px-3.5 py-1.5 text-xs sm:text-sm transition ${
              active
                ? 'font-bold text-white shadow-sm'
                : 'border border-[#EAE8F5] bg-[#F7F8FA] font-semibold text-[#4A4670] hover:border-[#D9D3F0] hover:bg-white'
            }`}
            style={active ? { background: 'linear-gradient(135deg, #3B66B8, #8156B4)' } : undefined}
          >
            {option.label}
            {option.count != null ? (
              <span className="ml-1.5 text-xs opacity-75">({option.count})</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
